'use client';

import {useState, useRef, useEffect} from 'react';
import {ChevronDown} from 'lucide-react';
import {renderTextWithLinks} from '@/lib/render-markdown-links';

interface ExpandableTextProps {
    text: string;
    showMoreLabel: string;
    showLessLabel: string;
}

const COLLAPSED_HEIGHT_EM = 7.5; // ~5 lines
const MD_BREAKPOINT = 768;

export function ExpandableText({text, showMoreLabel, showLessLabel}: ExpandableTextProps) {
    const [expanded, setExpanded] = useState(false);
    const [clamped, setClamped] = useState(false);
    const [fullHeight, setFullHeight] = useState(0);
    const [isLargeScreen, setIsLargeScreen] = useState(false);
    const measureRef = useRef<HTMLParagraphElement>(null);

    useEffect(() => {
        const checkScreen = () => setIsLargeScreen(window.innerWidth >= MD_BREAKPOINT);
        checkScreen();
        window.addEventListener('resize', checkScreen);
        return () => window.removeEventListener('resize', checkScreen);
    }, []);

    useEffect(() => {
        const el = measureRef.current;
        if (!el) return;
        // Defer the layout reads to the next frame. Reading scrollHeight +
        // getComputedStyle directly inside useEffect (which fires before paint
        // during hydration) forces a sync layout; rAF lets the browser finish
        // the natural layout pass first so the reads are free.
        const raf = requestAnimationFrame(() => {
            const current = measureRef.current;
            if (!current) return;
            const scrollH = current.scrollHeight;
            setFullHeight(scrollH);
            const fontSize = parseFloat(getComputedStyle(current).fontSize);
            const collapsedPx = COLLAPSED_HEIGHT_EM * fontSize;
            setClamped(scrollH > collapsedPx + 1);
        });
        return () => cancelAnimationFrame(raf);
    }, [text]);

    return (
        <div className="mt-3">
            <p
                ref={measureRef}
                className="text-sm sm:text-base md:text-lg text-white/85 drop-shadow-md overflow-hidden max-h-[7.5em] md:max-h-none transition-[max-height] duration-500 ease-in-out md:transition-none"
                style={!isLargeScreen && expanded ? {maxHeight: `${fullHeight}px`} : undefined}
            >
                {renderTextWithLinks(text)}
            </p>
            {clamped && !isLargeScreen && (
                <button
                    onClick={() => setExpanded((v) => !v)}
                    aria-label={expanded ? showLessLabel : showMoreLabel}
                    aria-expanded={expanded}
                    className="mt-1 text-white/60 hover:text-white/90 transition-all"
                >
                    <ChevronDown className={`h-5 w-5 transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`} />
                </button>
            )}
        </div>
    );
}
